import React, { useState } from 'react';
import styled from 'styled-components';
import { AuthState, withAuth } from 'context/auth';
import { AlertsState } from 'context/alerts';
import {
  Button,
  Checkbox,
  Jumbotron,
  SandboxTerms,
  Modal,
  UnstyledButton,
} from 'components';
import { postAcceptTerms } from 'utils/api';
import { media } from 'styles/media';
import { useNavigate } from 'react-router-dom';

const StyledWelcome = styled.h2`
  text-transform: uppercase;
  margin-top: 0;
  margin-bottom: 10px;
  font-size: ${p => p.theme.fontSize['2xl']};
  text-align: center;
`;
const StyledTitle = styled.h1`
  margin-top: 0;
  margin-bottom: 0;
  font-size: ${p => p.theme.fontSize.hero};
  line-height: 1;
  text-align: center;
`;
const StyledDarkContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${p => p.theme.colors.deepGreyTransparent};
  padding: 30px 50px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  max-width: 700px;
  width: 100%;

  ${media.md} {
    padding: 24px 16px;
  }

  h3 {
    font-size: ${p => p.theme.fontSize['2xl']};
    margin-bottom: 42px;
  }
`;
const StyledAcceptTerms = styled.div`
  margin-left: auto;
  margin-right: auto;
`;
const StyledOpenSandboxTermsModal = styled(UnstyledButton)`
  margin-top: 84px;
  outline: none;
  font-size: ${p => p.theme.fontSize.base};
  text-decoration: underline;
`;
const StyledAcceptTermsButton = styled(Button)`
  margin-top: 42px;
`;
const VisuallyHidden = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
const StyledDarkContainerHeader = styled.h3`
  margin-top: 0;
`;

interface Props {
  auth: AuthState;
  alerts: AlertsState;
}

const SandboxPage = (props: Props) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userHasAcceptedTerms, setUserHasAcceptedTerms] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const acceptTerms = async (termsAccepted: boolean, sandboxId: string) => {
    if (!termsAccepted) {
      return;
    }
    setLoading(true);

    try {
      await postAcceptTerms(props.auth, sandboxId);
      setTermsAccepted(true);
      setUserHasAcceptedTerms(true);
      props.auth.acceptSandboxTerms();
    } catch (err: any) {
      props.alerts.setMessage(err, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const renderTermsModal = () => {
    const sandbox = props.auth.sandbox;
    if (sandbox?.termsAccepted === false && userHasAcceptedTerms === false)
      return null;

    const mailtoLink = `mailto:${props.auth.sandbox?.themeInformation?.helpdesk_email}`;
    return (
      <>
        <StyledOpenSandboxTermsModal onClick={() => setModalOpen(true)}>
          View {process.env.REACT_APP_SANDBOX_NAME} Terms of Use
        </StyledOpenSandboxTermsModal>
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          ariaLabelledBy="terms-modal-title"
        >
          <VisuallyHidden id="terms-modal-title">
            Sandbox Terms of Use
          </VisuallyHidden>
          <SandboxTerms loadTermsOnInit />
        </Modal>
        <p>
          Contact us,{' '}
          <a href={mailtoLink}>
            {props.auth.sandbox?.themeInformation?.helpdesk_email}
          </a>
        </p>
      </>
    );
  };

  const renderLowerHalf = () => {
    const sandbox = props.auth.sandbox;
    if (sandbox?.termsAccepted === false && userHasAcceptedTerms === false) {
      return (
        <StyledDarkContainer id="sandbox-dark-container">
          <StyledAcceptTerms>
            <SandboxTerms>
              <Checkbox
                checked={termsAccepted}
                onChange={(e: any) => setTermsAccepted(e.target.checked)}
                disabled={loading}
              >
                I accept {process.env.REACT_APP_SANDBOX_NAME} Terms of Use
              </Checkbox>
              <StyledAcceptTermsButton
                onClick={() => acceptTerms(termsAccepted, sandbox.id)}
                disabled={!termsAccepted || loading}
              >
                Accept
              </StyledAcceptTermsButton>
            </SandboxTerms>
          </StyledAcceptTerms>
        </StyledDarkContainer>
      );
    }

    return (
      <StyledDarkContainer id="sandbox-dark-container">
        <StyledDarkContainerHeader>
          Genome and phenome data and a variety of tools are waiting
        </StyledDarkContainerHeader>
        <Button
          onClick={() => {
            navigate(`/${props.auth.sandbox?.id}/vm`);
          }}
        >
          Open sandbox desktop
        </Button>
        {renderTermsModal()}
      </StyledDarkContainer>
    );
  };

  const renderUpperHalf = () => {
    const title =
      props.auth.sandbox?.themeInformation?.title || props.auth.sandbox?.name;
    return (
      <>
        <StyledWelcome id="sandbox-welcome-text">Welcome to</StyledWelcome>
        <StyledTitle id="sandbox-title">{title}</StyledTitle>
      </>
    );
  };

  return (
    <>
      <Jumbotron
        loading={props.auth.loggedIn === undefined}
        upperHalf={renderUpperHalf()}
        lowerHalf={renderLowerHalf()}
        backgroundImage={props.auth.sandbox?.themeInformation?.background_url}
      />
    </>
  );
};
export default withAuth(SandboxPage);
